'use client';

import { formatDateTimePartsInGmtPlus3 } from '@/shared/date-time-gmt-plus-3';
import { FC, useMemo } from 'react';

export type ProductAnalysisReviewsTimelinePoint = {
    startTimestamp: number;
    endTimestamp: number;
    reviewsCount: number;
};

type ProductAnalysisReviewsTimelineChartProps = {
    points: ProductAnalysisReviewsTimelinePoint[];
    aggregationWindowDays: number;
};

const CHART_WIDTH = 760;
const CHART_HEIGHT = 300;
const PADDING_TOP = 20;
const PADDING_RIGHT = 20;
const PADDING_BOTTOM = 42;
const PADDING_LEFT = 42;

const toRounded = (value: number): number => {
    return Number(value.toFixed(2));
};

const formatDateLabel = (timestamp: number): string => {
    const parts = formatDateTimePartsInGmtPlus3(new Date(timestamp));
    if (!parts) {
        return new Date(timestamp).toISOString().slice(0, 10);
    }

    return parts.date;
};

const ProductAnalysisReviewsTimelineChart: FC<ProductAnalysisReviewsTimelineChartProps> = ({
    points,
    aggregationWindowDays,
}) => {
    const chartData = useMemo(() => {
        const plotWidth = CHART_WIDTH - PADDING_LEFT - PADDING_RIGHT;
        const plotHeight = CHART_HEIGHT - PADDING_TOP - PADDING_BOTTOM;
        const maxCount = Math.max(1, ...points.map((point) => point.reviewsCount));

        const getX = (index: number): number => {
            if (points.length <= 1) {
                return PADDING_LEFT + plotWidth / 2;
            }

            return PADDING_LEFT + (index / (points.length - 1)) * plotWidth;
        };

        const getY = (count: number): number => {
            return PADDING_TOP + plotHeight - (Math.max(0, count) / maxCount) * plotHeight;
        };

        const linePoints = points.map((point, index) => ({
            ...point,
            x: getX(index),
            y: getY(point.reviewsCount),
        }));

        const linePath = linePoints.map((point) => `${toRounded(point.x)},${toRounded(point.y)}`).join(' ');
        const xLabelStep = Math.max(1, Math.ceil(points.length / 7));
        const yTickValues = [...new Set(new Array(5).fill(0).map((_, index) => Math.round((maxCount * index) / 4)))]
            .sort((left, right) => left - right)
            .map((value) => ({
                value,
                y: getY(value),
            }));

        return {
            linePath,
            linePoints,
            xLabelStep,
            yTickValues,
            plotTop: PADDING_TOP,
            plotBottom: PADDING_TOP + plotHeight,
            plotLeft: PADDING_LEFT,
            plotRight: PADDING_LEFT + plotWidth,
            maxCount,
        };
    }, [points]);

    return (
        <div className="w-full overflow-x-auto">
            <div className="min-w-[760px] rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="text-sm font-semibold text-slate-800">Линейный график количества отзывов во времени</h3>
                <p className="mt-1 text-xs text-slate-500">
                    Каждый point - количество отзывов в интервале агрегации ({aggregationWindowDays} дн.).
                </p>
                {points.length ? (
                    <div className="mt-4 flex justify-center">
                        <svg
                            width={CHART_WIDTH}
                            height={CHART_HEIGHT}
                            viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
                            role="img"
                            aria-label="Линейный график количества отзывов во времени"
                        >
                            <rect
                                x={chartData.plotLeft}
                                y={chartData.plotTop}
                                width={chartData.plotRight - chartData.plotLeft}
                                height={chartData.plotBottom - chartData.plotTop}
                                fill="#ffffff"
                            />
                            {chartData.yTickValues.map((tick) => (
                                <g key={`y-tick-${tick.value}`}>
                                    <line
                                        x1={chartData.plotLeft}
                                        y1={toRounded(tick.y)}
                                        x2={chartData.plotRight}
                                        y2={toRounded(tick.y)}
                                        stroke="#e2e8f0"
                                        strokeWidth="1"
                                    />
                                    <text
                                        x={chartData.plotLeft - 8}
                                        y={toRounded(tick.y + 3)}
                                        fontSize="10"
                                        fill="#64748b"
                                        textAnchor="end"
                                    >
                                        {tick.value}
                                    </text>
                                </g>
                            ))}

                            <line
                                x1={chartData.plotLeft}
                                y1={chartData.plotBottom}
                                x2={chartData.plotRight}
                                y2={chartData.plotBottom}
                                stroke="#94a3b8"
                                strokeWidth="1"
                            />
                            <line
                                x1={chartData.plotLeft}
                                y1={chartData.plotTop}
                                x2={chartData.plotLeft}
                                y2={chartData.plotBottom}
                                stroke="#94a3b8"
                                strokeWidth="1"
                            />

                            <polyline
                                points={chartData.linePath}
                                fill="none"
                                stroke="#2563eb"
                                strokeWidth="2.5"
                                strokeLinejoin="round"
                                strokeLinecap="round"
                            />

                            {chartData.linePoints.map((point, index) => (
                                <g key={`line-point-${index}`}>
                                    <circle
                                        cx={toRounded(point.x)}
                                        cy={toRounded(point.y)}
                                        r="3.5"
                                        fill="#2563eb"
                                        stroke="#ffffff"
                                        strokeWidth="1"
                                    />
                                    {(index % chartData.xLabelStep === 0 ||
                                        index === chartData.linePoints.length - 1) && (
                                        <text
                                            x={toRounded(point.x)}
                                            y={CHART_HEIGHT - 8}
                                            fontSize="9"
                                            fill="#64748b"
                                            textAnchor="middle"
                                        >
                                            {formatDateLabel(point.startTimestamp)}
                                        </text>
                                    )}
                                </g>
                            ))}

                            <text
                                x={chartData.plotLeft}
                                y={14}
                                fontSize="10"
                                fill="#475569"
                                textAnchor="start"
                            >{`Max: ${chartData.maxCount}`}</text>
                        </svg>
                    </div>
                ) : (
                    <div className="mt-4 flex min-h-[220px] items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-sm text-slate-600">
                        Нет отзывов в выбранном периоде.
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProductAnalysisReviewsTimelineChart;
